import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusPill } from "@/components/common";
import { api, errMsg, fmtDate, fmtDateTime } from "@/lib/api";

const CreditForm = ({ agencyId, onDone }) => {
  const [f, setF] = useState({ action: "assign", amount: "", note: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/admin/agencies/${agencyId}/credits`, { ...f, amount: Number(f.amount) });
      toast.success("Credits updated");
      setF({ action: "assign", amount: "", note: "" });
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <form onSubmit={submit} className="grid grid-cols-2 gap-3">
      <Select value={f.action} onValueChange={(v) => setF({ ...f, action: v })}>
        <SelectTrigger data-testid="credit-action-select"><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="assign">Assign</SelectItem><SelectItem value="deduct">Deduct</SelectItem><SelectItem value="refund">Refund</SelectItem></SelectContent>
      </Select>
      <Input type="number" min={1} required placeholder="Amount" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="credit-amount-input" />
      <Input className="col-span-2" required minLength={3} placeholder="Reason (saved in audit log)" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="credit-note-input" />
      <Button type="submit" className="col-span-2" data-testid="credit-submit-button">Apply</Button>
    </form>
  );
};

const SubForm = ({ agencyId, onDone }) => {
  const [plans, setPlans] = useState([]);
  const [plan, setPlan] = useState("");
  useEffect(() => {
    api.get("/admin/plans").then((r) => setPlans(r.data));
  }, []);
  const activate = async () => {
    try {
      await api.post("/admin/subscriptions", { agency_id: agencyId, plan_id: plan });
      toast.success("Subscription activated");
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <div className="flex gap-2">
      <Select value={plan} onValueChange={setPlan}>
        <SelectTrigger data-testid="agency-plan-select"><SelectValue placeholder="Select plan" /></SelectTrigger>
        <SelectContent>{plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({p.duration_days}d)</SelectItem>)}</SelectContent>
      </Select>
      <Button disabled={!plan} onClick={activate} data-testid="agency-activate-sub-button">Activate</Button>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="py-5 border-b last:border-0"><p className="font-display font-semibold text-sm mb-3">{title}</p>{children}</div>
);

export default function AgencyDrawer({ agencyId, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const load = useCallback(() => {
    if (agencyId) api.get(`/admin/agencies/${agencyId}`).then((r) => setD(r.data));
  }, [agencyId]);

  useEffect(() => {
    setD(null);
    load();
  }, [load]);

  const changed = () => {
    load();
    onChanged();
  };

  const toggleStatus = async () => {
    const status = d.agency.status === "active" ? "suspended" : "active";
    try {
      await api.put(`/admin/agencies/${agencyId}/status`, { status });
      toast.success(`Agency ${status}`);
      changed();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <Sheet open={!!agencyId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="agency-drawer">
        {d && (
          <>
            <SheetHeader><SheetTitle className="font-display text-left">{d.agency.name}</SheetTitle></SheetHeader>
            <p className="text-sm text-muted-foreground">{d.owner?.name} · {d.owner?.email}</p>
            <Section title="Account access">
              <div className="flex items-center justify-between"><StatusPill status={d.agency.status} />
                <Button size="sm" variant={d.agency.status === "active" ? "destructive" : "default"} onClick={toggleStatus} data-testid="agency-toggle-status-button">{d.agency.status === "active" ? "Suspend" : "Reactivate"}</Button></div>
            </Section>
            <Section title={`Credits — balance ${d.credit_balance}`}><CreditForm agencyId={agencyId} onDone={changed} /></Section>
            <Section title="Subscription">
              <p className="text-sm mb-3" data-testid="agency-drawer-subscription">{d.subscription ? `${d.subscription.plan_name} until ${fmtDate(d.subscription.end_at)}` : "No active subscription"}</p>
              <SubForm agencyId={agencyId} onDone={changed} />
            </Section>
            <Section title="Recent credit transactions">
              {d.transactions.length === 0 ? <p className="text-sm text-muted-foreground">None</p> : d.transactions.slice(0, 15).map((t) => (
                <div key={t.id} className="flex justify-between text-sm py-1.5"><span><span className="font-mono text-xs uppercase">{t.type}</span> <span className="text-muted-foreground">· {t.note}</span></span>
                  <span className={`font-mono ${t.amount > 0 ? "text-emerald-600" : "text-rose-600"}`}>{t.amount > 0 ? "+" : ""}{t.amount}</span></div>
              ))}
            </Section>
            <Section title="Payments">
              {d.payments.length === 0 ? <p className="text-sm text-muted-foreground">None</p> : d.payments.slice(0, 10).map((p) => (
                <div key={p.id} className="flex justify-between items-center text-sm py-1.5"><span>{p.item_name} <span className="text-xs text-muted-foreground">{fmtDateTime(p.created_at)}</span></span><StatusPill status={p.status} /></div>
              ))}
            </Section>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
